import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useParams, useNavigate, Link } from "react-router-dom";
import { surveysService } from "../services/surveys";
import { responsesService } from "../services/responses";
import { useAuthStore } from "../store/authStore";

export default function ResponseSubmitPage() {
  const { id } = useParams() as { id: string };
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { user } = useAuthStore();

  const [responses, setResponses] = useState<Record<string, string>>({});
  const [startTime] = useState(Date.now());
  const [error, setError] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);

  const { data, isLoading, error: queryError } = useQuery({
    queryKey: ["survey", id],
    queryFn: async () => {
      const result = await surveysService.get(id!);
      return result;
    },
    enabled: !!id,
  });

  if (isLoading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="text-lg text-gray-400">Loading survey...</div>
      </div>
    );
  }

  if (queryError || !data?.survey) {
    return (
      <div className="card">
        <p className="text-red-400">
          Error loading survey: {(queryError as Error)?.message || "Survey not found"}
        </p>
      </div>
    );
  }

  const survey = data.survey;

  if (survey.status !== "active" && survey.status !== "collecting") {
    return (
      <div className="card text-center py-12">
        <h2 className="text-2xl font-bold text-gray-100 mb-4">Survey Not Available</h2>
        <p className="text-gray-400 mb-6">This survey is not currently accepting responses.</p>
        <Link to="/surveys" className="btn btn-primary">
          Back to Surveys
        </Link>
      </div>
    );
  }

  const questions = survey.questions || [];
  const answeredCount = Object.keys(responses).length;
  const isComplete = answeredCount === questions.length;

  const handleOptionSelect = (questionId: string, optionId: string) => {
    setResponses((prev) => ({ ...prev, [questionId]: optionId }));
    setError("");
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");

    if (!isComplete) {
      setError("Please answer all questions before submitting");
      return;
    }

    const responseTimeSeconds = Math.floor((Date.now() - startTime) / 1000);

    // Prepare responses for submission
    const responsesData = questions.map((question) => ({
      question_id: question.id,
      selected_option_id: responses[question.id],
      response_time_seconds: responseTimeSeconds,
    }));

    setIsSubmitting(true);

    try {
      await responsesService.respondToSurvey(id, responsesData);
      queryClient.invalidateQueries({ queryKey: ["survey", id] });
      queryClient.invalidateQueries({ queryKey: ["responses"] });
      navigate(`/surveys/${id}/complete`);
    } catch (err: any) {
      setError(err.response?.data?.error || "Failed to submit response");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="max-w-3xl mx-auto">
      {/* Back Link */}
      <div className="mb-6">
        <Link to={`/surveys/${id}`} className="text-primary-300 hover:text-primary-200 text-sm">
          ← Back to survey details
        </Link>
      </div>

      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-100 mb-2">{survey.title}</h1>
        {survey.description && (
          <p className="text-gray-400">{survey.description}</p>
        )}
      </div>

      {/* Progress Bar */}
      <div className="mb-8">
        <div className="flex justify-between text-sm text-gray-400 mb-2">
          <span>Progress</span>
          <span>{answeredCount} / {questions.length} questions</span>
        </div>
        <div className="w-full bg-dark-800 rounded-full h-2">
          <div
            className="bg-primary-500 h-2 rounded-full transition-all"
            style={{ width: `${(answeredCount / questions.length) * 100}%` }}
          />
        </div>
      </div>

      {/* Reward Info */}
      {survey.reward_per_response && (
        <div className="card bg-blue-500/10 border-blue-500/20 mb-8">
          <p className="text-blue-300 text-sm">
            <span className="font-medium">💰 Potential Reward:</span> {survey.reward_per_response} points for your response
          </p>
        </div>
      )}

      {error && (
        <div className="mb-6 p-4 bg-red-500/10 border border-red-500/20 text-red-400 rounded-lg">
          {error}
        </div>
      )}

      <form onSubmit={handleSubmit}>
        <div className="space-y-8">
          {questions.map((question, qIndex) => (
            <div key={question.id} className="card">
              <h3 className="text-xl font-bold text-gray-100 mb-4">
                {qIndex + 1}. {question.question_text}
              </h3>

              <div className="space-y-3">
                {question.options?.map((option) => (
                  <label
                    key={option.id}
                    className={`flex items-center p-4 border-2 rounded-lg cursor-pointer transition-colors ${
                      responses[question.id] === option.id
                        ? "border-primary-500 bg-primary-500/10"
                        : "border-dark-border hover:border-dark-600"
                    }`}
                  >
                    <input
                      type="radio"
                      name={`question-${question.id}`}
                      value={option.id}
                      checked={responses[question.id] === option.id}
                      onChange={() => handleOptionSelect(question.id, option.id)}
                      className="mr-3"
                    />
                    <span className="text-gray-200">{option.option_text}</span>
                  </label>
                ))}
              </div>
            </div>
          ))}
        </div>

        {/* Submit Button */}
        <div className="mt-8 flex justify-between">
          <button
            type="button"
            onClick={() => navigate(`/surveys/${id}`)}
            className="btn btn-secondary"
            disabled={isSubmitting}
          >
            Cancel
          </button>
          <button
            type="submit"
            className="btn btn-primary"
            disabled={!isComplete || isSubmitting}
          >
            {isSubmitting ? "Submitting..." : "Submit Response"}
          </button>
        </div>
      </form>
    </div>
  );
}
